import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Avatar,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardList,
  CardListItem,
  DataTable,
  EmptyState,
  PageHeader,
  StatusChip,
  Tabs,
  Tag,
  Timeline,
  TimelineAttachment,
} from '@/design/components'
import type { Column, TimelineItem } from '@/design/components'
import { useBreadcrumb } from '@/app/Breadcrumb'
import { useMe } from '@/app/MeContextProvider'
import { ApiError } from '@/lib/api'
import {
  EMPTY,
  formatDate,
  formatDueLabel,
  formatMoney,
  daysUntil,
  orDash,
  plural,
} from '@/lib/format'
import { cn } from '@/lib/cn'
import {
  deadlineDate,
  deadlineTitle,
  documentDate,
  documentTitle,
  toItems,
  useCaseDeadlines,
  useCaseDocuments,
  useCaseQuery,
  useCaseTasks,
  useCaseTimeEntries,
} from '../hooks'
import type { TaskLike, TimeEntryLike } from '../hooks'
import { PARTY_LABEL, ROLE_LABEL, STATUS_LABEL, STATUS_VARIANT } from '../status'
import type { CaseDetail, CaseEvent, CaseParty, DeadlineLike, DocumentLike } from '../types'

type TabKey = 'overview' | 'documents' | 'deadlines' | 'tasks' | 'time'

/**
 * Statusy zamykające termin — lokalna kopia (do modułu `deadlines` nie importujemy,
 * kontrakt §4; odpowiednik `CLOSED_STATUSES` w `app/modules/deadlines/models.py`).
 */
const CLOSED_DEADLINE_STATUSES = ['done', 'cancelled']

function isOpenDeadline(deadline: DeadlineLike): boolean {
  return !deadline.status || !CLOSED_DEADLINE_STATUSES.includes(deadline.status)
}

/** Etykiety statusów zadań — lokalna kopia (do modułu `tasks` nie importujemy, kontrakt §4). */
const TASK_STATUS_LABEL: Record<string, string> = {
  open: 'Otwarte',
  in_progress: 'W realizacji',
  done: 'Zakończone',
  cancelled: 'Anulowane',
}

/** 90 → „1 h 30 min" — lokalna kopia (do modułu `timesheet` nie importujemy, kontrakt §4). */
function formatMinutesShort(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || Number.isNaN(minutes)) return EMPTY
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h === 0) return `${m} min`
  if (m === 0) return `${h} h`
  return `${h} h ${m} min`
}

/** Kropka na osi czasu wg źródła wpisu (mockup: dokument = złota, przyjęcie = zielona). */
function dotFor(event: CaseEvent, isOldest: boolean): TimelineItem['dot'] {
  if (isOldest) return 'green'
  if (event.kind === 'document') return 'gold'
  return 'navy'
}

export function CaseDetailPage() {
  const { caseId } = useParams<{ caseId: string }>()
  const [tab, setTab] = useState<TabKey>('overview')
  const navigate = useNavigate()
  const { moduleKeys, can } = useMe()

  const hasTimesheet = moduleKeys.includes('timesheet')
  const hasTasks = moduleKeys.includes('tasks')

  /**
   * Kliknięcie wiersza prowadzi na ekran modułu, do którego dany zasób należy —
   * tak samo jak na `/zadania`, `/czas`, `/terminy` i w skrzynce wpływu. Gdy moduł
   * docelowy jest niewidoczny dla roli albo brakuje uprawnienia, przekazujemy
   * `undefined`: wiersz NIE udaje wtedy klikalnego (DataTable nie doda kursora i hovera).
   */
  // Bramka na `documents.view`, NIE na `documents.verify`: ekran weryfikacji otwiera
  // każdy, kto widzi dokument — przyciski akcji i tak pokazują się tylko uprawnionym.
  // Na `verify` Partner (który ma tam `NO`) nie mógł kliknąć własnych dokumentów.
  const openDocument = moduleKeys.includes('documents') && can('documents.view')
    ? (id: number | string) => navigate(`/dokumenty/weryfikacja/${id}`)
    : undefined
  const openDeadline = moduleKeys.includes('deadlines') && can('deadlines.view')
    ? (id: number | string) => navigate(`/terminy?termin=${id}`)
    : undefined
  const openTasks = hasTasks && can('tasks.view')
    ? (id: number) => navigate(`/zadania?sprawa=${id}`)
    : undefined
  const openTime = hasTimesheet && can('timesheet.view')
    ? (id: number) => navigate(`/czas?sprawa=${id}`)
    : undefined

  const query = useCaseQuery(caseId)
  const deadlines = useCaseDeadlines(caseId)
  const documents = useCaseDocuments(caseId)
  const timeEntries = useCaseTimeEntries(caseId, hasTimesheet)
  const tasks = useCaseTasks(caseId, hasTasks)

  const item = query.data
  // Okruszki: twarde spacje, bo rdzeniowy `useBreadcrumb` dzieli etykiety po spacji.
  useBreadcrumb(['Sprawy', (item?.signature ?? 'Karta sprawy').replace(/ /g, ' ')])

  const deadlineItems = useMemo(() => toItems<DeadlineLike>(deadlines.data), [deadlines.data])
  const documentItems = useMemo(() => toItems<DocumentLike>(documents.data), [documents.data])
  const timeItems = useMemo(() => toItems<TimeEntryLike>(timeEntries.data), [timeEntries.data])
  const taskItems = useMemo(() => toItems<TaskLike>(tasks.data), [tasks.data])

  /**
   * Chip w nagłówku = ten sam termin, który rejestr spraw pokazuje w kolumnie
   * NAJBLIŻSZY TERMIN (`GET /api/deadlines/upcoming`): najwcześniejszy NIEZAMKNIĘTY.
   * Wcześniej brał też terminy zakończone i anulowane, więc karta sprawy potrafiła
   * krzyczeć „termin 11 dni temu" o obowiązku dawno wykonanym.
   */
  const nextDeadline = useMemo(() => {
    const dated = deadlineItems
      .filter(isOpenDeadline)
      .map((deadline) => ({ deadline, date: deadlineDate(deadline) }))
      .filter((entry): entry is { deadline: DeadlineLike; date: string } => Boolean(entry.date))
      .sort((a, b) => a.date.localeCompare(b.date))
    return dated[0] ?? null
  }, [deadlineItems])

  if (query.isPending) {
    return <PageHeader title="Wczytywanie…" back={{ to: '/sprawy', label: 'Sprawy' }} />
  }

  if (query.isError || !item) {
    const notFound = query.error instanceof ApiError && (query.error.isNotFound || query.error.isForbidden)
    return (
      <>
        <PageHeader title="Sprawa" back={{ to: '/sprawy', label: 'Sprawy' }} />
        <Card>
          <EmptyState
            size="page"
            title={notFound ? 'Nie znaleziono sprawy' : 'Nie udało się wczytać sprawy'}
            description={
              notFound
                ? 'Sprawa nie istnieje albo jest poza Twoim zakresem widoczności.'
                : 'Spróbuj ponownie za chwilę.'
            }
          />
        </Card>
      </>
    )
  }

  return (
    <>
      <PageHeader
        back={{ to: '/sprawy', label: 'Sprawy' }}
        eyebrow={
          <>
            <span className="t-mono text-ink">{item.signature}</span>
            <StatusChip variant={STATUS_VARIANT[item.status]}>{STATUS_LABEL[item.status]}</StatusChip>
            {nextDeadline ? (
              <StatusChip variant={(daysUntil(nextDeadline.date) ?? 99) <= 12 ? 'danger' : 'warning'}>
                {(daysUntil(nextDeadline.date) ?? 0) < 0
                  ? `Po terminie — ${formatDueLabel(nextDeadline.date)}`
                  : `Termin ${formatDueLabel(nextDeadline.date)}`}
              </StatusChip>
            ) : null}
          </>
        }
        title={item.title}
        subtitle={metaLine(item)}
        actions={
          <>
            {hasTasks ? (
              // Query param `sprawa` + `nowa=1` otwiera modal nowego zadania z tą sprawą.
              <Button
                variant="secondary"
                onClick={() => navigate(`/zadania?sprawa=${item.id}&nowa=1`)}
              >
                Dodaj zadanie
              </Button>
            ) : (
              <Button variant="secondary" disabled title="Moduł Zadania wyłączony">
                Dodaj zadanie
              </Button>
            )}
            {hasTimesheet ? (
              // Query param `sprawa` + `nowa=1` otwiera modal rejestracji z tą sprawą.
              <Button
                variant="secondary"
                onClick={() => navigate(`/czas?sprawa=${item.id}&nowa=1`)}
              >
                Rejestruj czas
              </Button>
            ) : (
              <Button variant="secondary" disabled title="Moduł Rejestracja czasu wyłączony">
                Rejestruj czas
              </Button>
            )}
            <Button variant="primary" disabled title="Moduł dokumentów — wkrótce">
              Nowe pismo
            </Button>
          </>
        }
      />

      <Tabs
        className="mb-5"
        value={tab}
        onChange={(key) => setTab(key as TabKey)}
        items={[
          { key: 'overview', label: 'Przegląd' },
          { key: 'documents', label: 'Dokumenty', count: documentItems.length || null },
          { key: 'deadlines', label: 'Terminy', count: deadlineItems.length || null },
          hasTasks
            ? { key: 'tasks', label: 'Zadania', count: taskItems.length || null }
            : { key: 'tasks', label: <span title="wkrótce">Zadania</span>, disabled: true },
          hasTimesheet
            ? { key: 'time', label: 'Czas i koszty', count: timeItems.length || null }
            : { key: 'time', label: <span title="wkrótce">Czas i koszty</span>, disabled: true },
          { key: 'mail', label: <span title="wkrótce">Korespondencja</span>, disabled: true },
        ]}
      />

      {tab === 'overview' ? (
        <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
          <div className="flex min-w-0 flex-col gap-5">
            <PartiesCard parties={item.parties} />
            <HistoryCard events={item.events} />
          </div>
          <div className="flex min-w-0 flex-col gap-5">
            <TeamCard item={item} />
            <DeadlinesPanel items={deadlineItems} available={deadlines.data != null} />
            <DocumentsPanel items={documentItems} available={documents.data != null} />
          </div>
        </div>
      ) : null}

      {tab === 'documents' ? (
        <DocumentsTable items={documentItems} available={documents.data != null} onOpen={openDocument} />
      ) : null}

      {tab === 'deadlines' ? (
        <DeadlinesList items={deadlineItems} available={deadlines.data != null} onOpen={openDeadline} />
      ) : null}

      {tab === 'tasks' ? (
        <TasksTable
          items={taskItems}
          available={tasks.data != null}
          onOpen={openTasks ? () => openTasks(item.id) : undefined}
        />
      ) : null}

      {tab === 'time' ? (
        <TimeEntriesTable
          items={timeItems}
          totalMinutes={timeEntries.data?.total_minutes ?? null}
          available={timeEntries.data != null}
          onOpen={openTime ? () => openTime(item.id) : undefined}
        />
      ) : null}
    </>
  )
}

/** „Sąd Okręgowy w Warszawie, XXIV Wydział Cywilny · WPS 2 480 000,00 zł · wpływ 14.03.2025" */
function metaLine(item: CaseDetail): string {
  const parts: string[] = []
  if (item.court) parts.push(item.court)
  else if (item.court_short) parts.push(item.court_short)
  if (item.wps !== null && item.wps !== undefined) parts.push(`WPS ${formatMoney(item.wps)}`)
  if (item.opened_at) parts.push(`wpływ ${formatDate(item.opened_at)}`)
  return parts.join(' · ')
}

/* ------------------------------------------------- strony postępowania */

function PartiesCard({ parties }: { parties: CaseParty[] }) {
  return (
    <Card>
      <CardHeader title="Strony postępowania" />
      <CardBody>
        {parties.length === 0 ? (
          <EmptyState title="Nie wskazano stron" description="Dane stron uzupełnisz przy edycji sprawy." />
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {parties.map((party) => (
              <div key={party.id} className="rounded border border-line-soft p-4">
                <p className="t-label">{PARTY_LABEL[party.kind]}</p>
                <p className="mt-2 text-md font-semibold text-ink">{party.name}</p>
                {party.krs || party.nip ? (
                  <p className="mt-1.5 text-small text-muted">
                    {party.krs ? (
                      <>
                        KRS <span className="font-mono text-ink">{party.krs}</span>
                      </>
                    ) : null}
                    {party.krs && party.nip ? <span className="px-2">·</span> : null}
                    {party.nip ? (
                      <>
                        NIP <span className="font-mono text-ink">{party.nip}</span>
                      </>
                    ) : null}
                  </p>
                ) : null}
                {party.representative ? (
                  <p className="mt-3 border-t border-line-soft pt-3 text-small text-muted">
                    Pełnomocnik: {party.representative}
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  )
}

/* ---------------------------------------------------- historia sprawy */

function HistoryCard({ events }: { events: CaseEvent[] }) {
  const items: TimelineItem[] = events.map((event, index) => ({
    id: event.id,
    date: event.occurred_at,
    title: event.title,
    description: event.description ?? undefined,
    dot: dotFor(event, index === events.length - 1),
    children: event.attachment_name ? (
      <TimelineAttachment>{event.attachment_name}</TimelineAttachment>
    ) : undefined,
  }))

  return (
    <Card>
      <CardHeader
        title="Historia sprawy"
        action={
          <span className="text-micro text-muted">
            {events.length} {plural(events.length, 'zdarzenie', 'zdarzenia', 'zdarzeń')}
          </span>
        }
      />
      <CardBody>
        {items.length === 0 ? (
          <EmptyState title="Brak zdarzeń" description="Historia zapełni się wraz z obiegiem pism." />
        ) : (
          <Timeline items={items} />
        )}
      </CardBody>
    </Card>
  )
}

/* -------------------------------------------------------- zespół sprawy */

function TeamCard({ item }: { item: CaseDetail }) {
  const ordered = [...item.team].sort((a, b) => {
    const rank = (role: string) => (role === 'prowadzacy' ? 0 : role === 'nadzorujacy' ? 1 : 2)
    return rank(a.role_in_case) - rank(b.role_in_case) || a.full_name.localeCompare(b.full_name, 'pl')
  })

  return (
    <Card>
      <CardHeader title="Zespół sprawy" />
      {ordered.length === 0 ? (
        <EmptyState title="Nie przypisano zespołu" />
      ) : (
        <CardList>
          {ordered.map((member) => (
            <CardListItem key={member.id}>
              <div className="flex items-center gap-3">
                <Avatar name={member.full_name} size="lg" />
                <div className="min-w-0">
                  <p className="truncate text-base font-semibold text-ink">{member.full_name}</p>
                  <p className="mt-0.5 truncate text-small text-muted">
                    {/* „Paralegal · Paralegal" byłoby powtórzeniem — tytuł pokazujemy tylko,
                        gdy wnosi coś ponad rolę w sprawie. */}
                    {[
                      ROLE_LABEL[member.role_in_case],
                      member.title === ROLE_LABEL[member.role_in_case] ? null : member.title,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
              </div>
            </CardListItem>
          ))}
        </CardList>
      )}
    </Card>
  )
}

/* -------------------------------------------------- terminy i dokumenty */

function ModuleOffState({ what }: { what: string }) {
  return (
    <EmptyState
      title={`Moduł ${what} nie jest dostępny`}
      description="Włącz go w konfiguracji instancji (MODULES_ENABLED), aby zobaczyć te dane."
    />
  )
}

function DeadlinesPanel({ items, available }: { items: DeadlineLike[]; available: boolean }) {
  const sorted = [...items].sort((a, b) => (deadlineDate(a) ?? '').localeCompare(deadlineDate(b) ?? ''))
  return (
    <Card>
      <CardHeader title="Terminy w sprawie" />
      {!available ? (
        <ModuleOffState what="terminów" />
      ) : sorted.length === 0 ? (
        <EmptyState title="Brak terminów w sprawie" />
      ) : (
        <CardList>
          {sorted.map((deadline) => {
            const date = deadlineDate(deadline)
            const days = daysUntil(date)
            return (
              <CardListItem key={deadline.id}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-base font-semibold text-ink">{deadlineTitle(deadline)}</p>
                    <p className="mt-0.5 truncate text-small text-muted">
                      {[date ? formatDate(date) : 'bez daty', deadline.legal_basis]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  </div>
                  <StatusChip
                    size="sm"
                    variant={date === null ? 'neutral' : (days ?? 99) <= 12 ? 'danger' : 'warning'}
                    className="shrink-0"
                  >
                    {date ? formatDueLabel(date) : 'brak daty'}
                  </StatusChip>
                </div>
              </CardListItem>
            )
          })}
        </CardList>
      )}
    </Card>
  )
}

function DocumentsPanel({ items, available }: { items: DocumentLike[]; available: boolean }) {
  return (
    <Card>
      <CardHeader
        title="Dokumenty"
        action={
          available ? (
            <span className="text-micro text-muted">
              {items.length} {plural(items.length, 'plik', 'pliki', 'plików')}
            </span>
          ) : null
        }
      />
      {!available ? (
        <ModuleOffState what="dokumentów" />
      ) : items.length === 0 ? (
        <EmptyState title="Brak dokumentów w sprawie" />
      ) : (
        <CardList>
          {items.slice(0, 8).map((doc) => (
            <CardListItem key={doc.id}>
              <div className="flex items-center gap-3">
                <span
                  aria-hidden
                  className="block h-[30px] w-[26px] shrink-0 rounded-[2px] border border-line-soft bg-[repeating-linear-gradient(135deg,#eef1f4_0_4px,#f7f9fa_4px_8px)]"
                />
                <div className="min-w-0">
                  <p className="truncate text-base text-ink">{documentTitle(doc)}</p>
                  <p className="mt-0.5 truncate text-small text-muted">
                    {[documentDate(doc) ? formatDate(documentDate(doc)) : null, doc.status, doc.author]
                      .filter(Boolean)
                      .join(' · ') || EMPTY}
                  </p>
                </div>
              </div>
            </CardListItem>
          ))}
        </CardList>
      )}
    </Card>
  )
}

function DocumentsTable({
  items,
  available,
  onOpen,
}: {
  items: DocumentLike[]
  available: boolean
  /** `undefined` → wiersz nieklikalny (brak modułu dokumentów albo uprawnienia). */
  onOpen?: (id: number | string) => void
}) {
  const columns: Column<DocumentLike>[] = [
    {
      key: 'title',
      header: 'Dokument',
      render: (row) => <span className="font-medium text-ink">{documentTitle(row)}</span>,
    },
    { key: 'doc_type', header: 'Typ', width: '160px', render: (row) => orDash(row.doc_type) },
    {
      key: 'date',
      header: 'Data',
      width: '120px',
      render: (row) => formatDate(documentDate(row)),
    },
    { key: 'status', header: 'Status', width: '150px', render: (row) => orDash(row.status) },
    {
      key: 'pages',
      header: 'Stron',
      width: '80px',
      align: 'right',
      render: (row) => orDash(row.pages),
    },
  ]

  if (!available) {
    return (
      <Card>
        <ModuleOffState what="dokumentów" />
      </Card>
    )
  }

  return (
    <DataTable<DocumentLike>
      columns={columns}
      rows={items}
      rowKey={(row) => row.id}
      onRowClick={onOpen ? (row) => onOpen(row.id) : undefined}
      empty={<EmptyState title="Brak dokumentów" description="W tej sprawie nie zarejestrowano jeszcze pism." />}
    />
  )
}

/* --------------------------------------------------------------- zadania */

function TasksTable({
  items,
  available,
  onOpen,
}: {
  items: TaskLike[]
  available: boolean
  /** Przejście na ekran „Zadania" z tą sprawą; `undefined` → wiersz nieklikalny. */
  onOpen?: () => void
}) {
  if (!available) {
    return (
      <Card>
        <ModuleOffState what="zadań" />
      </Card>
    )
  }

  const columns: Column<TaskLike>[] = [
    {
      key: 'title',
      header: 'Zadanie',
      render: (row) => (
        <div className="min-w-0 leading-tight">
          <p className="truncate font-medium text-ink">{row.title ?? `Zadanie #${row.id}`}</p>
          {row.description ? (
            <p className="mt-0.5 max-w-[420px] truncate text-small text-muted">{row.description}</p>
          ) : null}
        </div>
      ),
    },
    {
      key: 'assignee',
      header: 'Przypisany',
      width: '170px',
      render: (row) => orDash(row.assignee?.short_name ?? row.assignee?.name),
    },
    {
      key: 'due_date',
      header: 'Termin',
      width: '150px',
      render: (row) => {
        if (!row.due_date) return <span className="text-muted">{EMPTY}</span>
        const days = daysUntil(row.due_date)
        const open = row.status === 'open' || row.status === 'in_progress'
        return (
          <span className={cn('whitespace-nowrap', open && (days ?? 0) < 0 ? 'font-semibold text-danger' : 'text-ink')}>
            {formatDate(row.due_date)} · {formatDueLabel(row.due_date)}
          </span>
        )
      },
    },
    {
      key: 'status',
      header: 'Status',
      width: '130px',
      render: (row) => orDash(row.status ? (TASK_STATUS_LABEL[row.status] ?? row.status) : null),
    },
  ]

  return (
    <DataTable<TaskLike>
      columns={columns}
      rows={items}
      rowKey={(row) => row.id}
      onRowClick={onOpen ? () => onOpen() : undefined}
      empty={
        <EmptyState
          title="Brak zadań w sprawie"
          description="Zadania dodasz przyciskiem „Dodaj zadanie” w nagłówku karty."
        />
      }
    />
  )
}

/* -------------------------------------------------------- czas i koszty */

function TimeEntriesTable({
  items,
  totalMinutes,
  available,
  onOpen,
}: {
  items: TimeEntryLike[]
  totalMinutes: number | null
  available: boolean
  /** Przejście na ekran „Czas pracy" z tą sprawą; `undefined` → wiersz nieklikalny. */
  onOpen?: () => void
}) {
  if (!available) {
    return (
      <Card>
        <ModuleOffState what="rejestracji czasu" />
      </Card>
    )
  }

  const columns: Column<TimeEntryLike>[] = [
    {
      key: 'work_date',
      header: 'Data',
      width: '110px',
      render: (row) => <span className="whitespace-nowrap">{formatDate(row.work_date)}</span>,
    },
    {
      key: 'user',
      header: 'Prawnik',
      width: '160px',
      render: (row) => orDash(row.user?.short_name ?? row.user?.name),
    },
    {
      key: 'description',
      header: 'Opis',
      render: (row) =>
        row.description ? (
          <span className="text-ink">{row.description}</span>
        ) : (
          <span className="text-muted">{EMPTY}</span>
        ),
    },
    {
      key: 'billable',
      header: 'Rozliczalny',
      width: '120px',
      render: (row) => (row.billable ? <Tag active>rozliczalny</Tag> : <Tag>wewnętrzny</Tag>),
    },
    {
      key: 'minutes',
      header: 'Czas',
      width: '110px',
      align: 'right',
      render: (row) => (
        <span className="t-mono whitespace-nowrap text-ink">{formatMinutesShort(row.minutes)}</span>
      ),
    },
  ]

  return (
    <div className="flex flex-col gap-3">
      {/* Suma z `total_minutes` (cały przefiltrowany zbiór policzony w SQL, nie strona). */}
      <div className="flex items-center justify-end gap-2 text-small text-muted">
        <span>
          {items.length} {plural(items.length, 'wpis', 'wpisy', 'wpisów')}
        </span>
        <span aria-hidden>·</span>
        <span>
          razem <span className="font-semibold text-ink">{formatMinutesShort(totalMinutes)}</span>
        </span>
      </div>
      <DataTable<TimeEntryLike>
        columns={columns}
        rows={items}
        rowKey={(row) => row.id}
        onRowClick={onOpen ? () => onOpen() : undefined}
        empty={
          <EmptyState
            title="Brak wpisów czasu"
            description="Czas zarejestrowany w tej sprawie pojawi się tutaj."
          />
        }
      />
    </div>
  )
}

function DeadlinesList({
  items,
  available,
  onOpen,
}: {
  items: DeadlineLike[]
  available: boolean
  /** Otwarcie terminu na ekranie „Terminy procesowe"; `undefined` → pozycja nieklikalna. */
  onOpen?: (id: number | string) => void
}) {
  if (!available) {
    return (
      <Card>
        <ModuleOffState what="terminów" />
      </Card>
    )
  }
  const sorted = [...items].sort((a, b) => (deadlineDate(a) ?? '').localeCompare(deadlineDate(b) ?? ''))
  return (
    <Card>
      <CardHeader title="Terminy w sprawie" />
      {sorted.length === 0 ? (
        <EmptyState title="Brak terminów" description="Terminy pojawią się po weryfikacji pism z pouczeniami." />
      ) : (
        <CardList>
          {sorted.map((deadline) => {
            const date = deadlineDate(deadline)
            const days = daysUntil(date)
            return (
              <CardListItem
                key={deadline.id}
                onClick={onOpen ? () => onOpen(deadline.id) : undefined}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="truncate text-base font-semibold text-ink">{deadlineTitle(deadline)}</p>
                    <p className="mt-0.5 text-small text-muted">
                      {[date ? formatDate(date) : 'bez daty', deadline.legal_basis, deadline.status]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  </div>
                  <span
                    className={cn(
                      'shrink-0 text-small',
                      date && (days ?? 99) <= 12 ? 'text-danger' : 'text-muted',
                    )}
                  >
                    {date ? formatDueLabel(date) : EMPTY}
                  </span>
                </div>
              </CardListItem>
            )
          })}
        </CardList>
      )}
    </Card>
  )
}
